/**
 * Fetch wrapper for the API, shared by the console and the client portal.
 *
 * There is no token to attach: the session is an HttpOnly cookie, so the browser
 * sends it and JavaScript cannot read it. That is the whole point — an XSS in this
 * console cannot exfiltrate a session the way a token in localStorage could.
 *
 * Neither cookie is named here. The browser decides which one a path gets, and
 * this module never sees either.
 */

const BASE = import.meta.env.VITE_API_BASE ?? "";

export interface ApiErrorDetail {
  path: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** Field-level issues from a zod rejection, when the API sent them. */
  get fieldErrors(): ApiErrorDetail[] {
    return Array.isArray(this.details)
      ? (this.details as ApiErrorDetail[]).filter((d) => typeof d?.message === "string")
      : [];
  }
}

type Method = "GET" | "POST" | "PUT" | "DELETE";

let unauthorizedHandler: (() => void) | null = null;

/**
 * Called when any request comes back 401, so a session that was revoked
 * server-side (password reset, deactivation, expiry) drops the console back to the
 * sign-in screen instead of leaving a shell that fails every request.
 *
 * One handler for both surfaces, because only one is ever mounted: `/portal/*` and
 * the console are separate branches of the route tree, so their providers never
 * overlap and cannot sign each other out.
 */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/**
 * A 401 from one of these is a wrong password, not a lost session. Firing the
 * handler would bounce someone who is already standing on the sign-in screen.
 */
const SIGN_IN_PATHS = new Set(["/api/auth/login", "/api/portal/auth/login"]);

async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    // The session cookie is same-origin (nginx or the Vite proxy serves both).
    credentials: "same-origin",
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  if (text !== "") {
    try {
      parsed = JSON.parse(text);
    } catch {
      // A non-JSON body from an API that only speaks JSON means something in front
      // of it answered — a proxy error page, most likely.
      throw new ApiError(res.status, `unexpected response from ${path}`);
    }
  }

  if (!res.ok) {
    if (res.status === 401 && !SIGN_IN_PATHS.has(path)) unauthorizedHandler?.();
    const payload = (parsed ?? {}) as { error?: string; details?: unknown };
    throw new ApiError(res.status, payload.error ?? `request failed (${res.status})`, payload.details);
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body ?? {}),
  del: <T>(path: string) => request<T>("DELETE", path),
};

/** Build a query string, dropping empty values so the URL reflects real filters. */
export function query(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const serialized = search.toString();
  return serialized === "" ? "" : `?${serialized}`;
}
